import { useMemo } from "react";

import { Box, Grid, Paper, Stack, Typography } from "@mui/material";

import { formatPrice } from "../../utility/formatters";

export default function GrandTotalSection({ year, title, debts = [] }) {
  const calculations = useMemo(() => {
    return debts.reduce(
      (acc, debt) => {
        const interestPaid = Number(debt.interest_paid) || 0;
        const principalPaid = Number(debt.principal_paid) || 0;
        const liabilityCurrent = Number(debt.liability_current) || 0;
        const liabilityNoncurrent = Number(debt.liability_intermediate || debt.liability_longterm) || 0;
        const principalTotal = Number(debt.principal_total) || 0;
        const interestAccrued = Number(debt.interest_accrued) || 0;

        return {
          interestPaid: acc.interestPaid + interestPaid,
          principalPaid: acc.principalPaid + principalPaid,
          currentPosition: acc.currentPosition + liabilityCurrent,
          noncurrentPosition: acc.noncurrentPosition + liabilityNoncurrent,
          totalPrincipal: acc.totalPrincipal + principalTotal,
          accruedInterest: acc.accruedInterest + interestAccrued,
        };
      },
      {
        interestPaid: 0,
        principalPaid: 0,
        currentPosition: 0,
        noncurrentPosition: 0,
        totalPrincipal: 0,
        accruedInterest: 0,
      }
    );
  }, [debts]);

  const { interestPaid, principalPaid, currentPosition, noncurrentPosition, totalPrincipal, accruedInterest } =
    calculations;

  return (
    <>
      <Typography variant="h6" sx={{ mb: "18px", mt: "2rem", fontWeight: "500 !important" }}>
        {title}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} xl={3}>
          <Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: "600 !important", mb: 1 }}>
              During {year}
            </Typography>

            <Paper
              sx={{
                borderRadius: 2,
                paddingX: "1.125rem",
                paddingY: "0.875rem",
                boxShadow: 0,
                flex: 1,
                display: "flex",
                gap: "1.125rem",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  gap: "1.125rem",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: "600 !important",
                    }}
                  >
                    {formatPrice(interestPaid, 2)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: "600", whiteSpace: "pre-line" }}>
                    {"INTEREST\nPAID"}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: "600 !important",
                    }}
                  >
                    {formatPrice(principalPaid, 0)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: "600", whiteSpace: "pre-line" }}>
                    {"PRINCIPAL\nPAID"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1, display: { xs: "none", md: "block", xl: "none" } }} />
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} xl={9}>
          <Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: "600 !important", mb: 1, mt: { xs: "1rem", md: 0 } }}>
              Year-End
            </Typography>

            <Paper
              sx={{
                borderRadius: 2,
                paddingX: "1.125rem",
                paddingY: "0.875rem",
                boxShadow: 0,
                flex: 1,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "1.125rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "1.125rem",
                  flex: { xs: "none", md: 2 },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: "600 !important",
                    }}
                  >
                    {formatPrice(currentPosition, 2)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: "600", whiteSpace: "pre-line" }}>
                    {"CURRENT\nLIABILITY"}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: "600 !important",
                    }}
                  >
                    {formatPrice(noncurrentPosition, 2)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: "600", whiteSpace: "pre-line" }}>
                    {"LONG-TERM\nLIABILITY"}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: "1.125rem",
                  flex: { xs: "none", md: 2 },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: "600 !important",
                    }}
                  >
                    {formatPrice(totalPrincipal, 0)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: "600", whiteSpace: "pre-line" }}>
                    {"TOTAL\nPRINCIPAL"}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "18px !important",
                        md: "24px !important",
                      },
                      fontWeight: "600 !important",
                    }}
                  >
                    {formatPrice(accruedInterest, 0)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: "600", whiteSpace: "pre-line" }}>
                    {"ACCRUED\nINTEREST"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
